"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Session = void 0;
const uuid_1 = require("uuid");
const main_1 = require("../../main");
const index_1 = __importDefault(require("../config/index"));
const local_data_1 = require("../helpers/local-data");
const logger_1 = require("./logger");
class Session {
    constructor(onReport, pingIntervalMS, disableAutoReporting) {
        Object.defineProperty(this, "onReport", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: onReport
        });
        Object.defineProperty(this, "pingIntervalMS", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: pingIntervalMS
        });
        Object.defineProperty(this, "disableAutoReporting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: disableAutoReporting
        });
        Object.defineProperty(this, "sessionData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pingTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sessionUpdateLock", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "elTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Date.now()
        });
    }
    static load(onReport, pingIntervalMS, disableAutoReporting) {
        if (pingIntervalMS !== undefined &&
            pingIntervalMS < index_1.default.sessionPingTimeMinMs) {
            logger_1.Logger.warn(`Min session ping time: ${index_1.default.sessionPingTimeMinMs}ms`);
            pingIntervalMS = index_1.default.sessionPingTimeMinMs;
        }
        const session = new Session(onReport, pingIntervalMS, disableAutoReporting);
        session.loadOrCreateSession();
        session.startPingInterval();
        return session;
    }
    get id() {
        var _a, _b;
        this.refresh();
        return (_b = (_a = this.sessionData) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
    }
    setId(id, expires = Date.now()) {
        if (!this.disableAutoReporting) {
            logger_1.Logger.error(`Session id cannot be set manually if auto session management is enabled`);
            return;
        }
        this.sessionData = {
            updated: Date.now(),
            created: Date.now(),
            expires,
            id,
        };
    }
    get expires() {
        var _a, _b;
        return (_b = (_a = this.sessionData) === null || _a === void 0 ? void 0 : _a.expires) !== null && _b !== void 0 ? _b : -1;
    }
    get expired() {
        return this.sessionData ? Date.now() > this.sessionData.expires : false;
    }
    get elapsedMS() {
        return this.sessionData
            ? Date.now() - this.sessionData.created
            : Date.now() - this.elTime;
    }
    async deregister() {
        this.stopPingInterval();
        if (!this.disableAutoReporting) {
            await this.reportSessionEnd();
        }
    }
    async refresh() {
        if (this.sessionUpdateLock ||
            this.disableAutoReporting ||
            !this.sessionData) {
            return;
        }
        if (!this.expired) {
            this.sessionData.updated = Date.now();
            this.sessionData.expires = this.expiryFromNow;
        }
        else {
            this.stopPingInterval();
            await this.reportSessionEnd();
            this.sessionData.updated = Date.now();
            this.sessionData.created = Date.now();
            this.sessionData.id = (0, uuid_1.v4)();
            this.sessionData.expires = this.expiryFromNow;
            await this.reportSessionStart();
            this.startPingInterval();
        }
        this.storedData = this.sessionData;
    }
    loadOrCreateSession() {
        if (this.disableAutoReporting) {
            return;
        }
        if (this.storedData && Date.now() < this.storedData.expires) {
            this.sessionData = this.storedData;
        }
        else {
            this.sessionData = {
                id: (0, uuid_1.v4)(),
                expires: this.expiryFromNow,
                created: Date.now(),
                updated: Date.now(),
            };
            this.reportSessionStart();
        }
    }
    stopPingInterval() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
        }
    }
    startPingInterval() {
        var _a;
        if (this.disableAutoReporting) {
            return;
        }
        this.stopPingInterval();
        this.pingTimer = setInterval(() => {
            this.reportSessionPing();
        }, (_a = this.pingIntervalMS) !== null && _a !== void 0 ? _a : index_1.default.sessionPingTimeDefaultMs);
    }
    set storedData(session) {
        if (local_data_1.LocalData.hasStorage) {
            local_data_1.LocalData.writeStorage(local_data_1.StorageKeys.sessionId, session);
        }
    }
    get storedData() {
        return local_data_1.LocalData.hasStorage
            ? local_data_1.LocalData.readStorage(local_data_1.StorageKeys.sessionId)
            : null;
    }
    get expiryFromNow() {
        const expiry = new Date();
        expiry.setTime(expiry.getTime() + index_1.default.sessionExpiryTimeMs);
        return expiry.getTime();
    }
    reportSessionStart() {
        return this.reportDTO(new main_1.SessionStartDTO());
    }
    reportSessionEnd() {
        return this.reportDTO(new main_1.SessionEndDTO());
    }
    async reportSessionPing() {
        if (!this.expired) {
            await this.reportDTO(new main_1.SessionPingDTO());
        }
    }
    async reportDTO(dto) {
        if (this.disableAutoReporting || !this.sessionData) {
            return;
        }
        dto.sessionId = this.sessionData.id;
        dto.context = index_1.default.sessionContextLabel;
        this.sessionUpdateLock = true;
        try {
            await this.onReport(dto);
        }
        catch (e) {
            throw e;
        }
        finally {
            this.sessionUpdateLock = false;
        }
    }
}
exports.Session = Session;
